import {useState, useEffect, useCallback} from 'react';
import config from '../../../oktaConfig';
import {useOktaAuth} from '@okta/okta-react';
import POSResponseData from 'features/POSRegisters/interfaces/POSResponseData';
import FetchResponseError from 'interfaces/FetchResponseError';
import useUserLogs from 'hooks/useUserLogs';
import POSState from 'features/POSRegisters/interfaces/POSState';
import {AlertType} from 'features/POSRegisters/interfaces/AlertInfo';

function usePOS(siteId: string | undefined) {
  const {authState, oktaAuth} = useOktaAuth();
  const [dataIsLoaded, setDataIsLoaded] = useState<boolean>(false);
  const [data, setData] = useState<POSResponseData | null>(null);
  const [error, setError] = useState<FetchResponseError | null>(null);
  const {dispatch: dispatchUserLog} = useUserLogs();

  const getScanLogPayload = useCallback(
    (state: POSState) => {
      const scanFeatures = new Set();
      const issuesDiscovered = new Set();

      const devicesList = {
        networkCircuits: state.networkCircuits,
        networkSwitches: state.networkSwitches,
        ipads: state.ipads,
        ipadsMini: state.ipadsMini,
        receiptPrinters: state.receiptPrinters,
        barcodeScanners: state.barcodeScanners,
        cashDrawers: state.cashDrawers,
        paymentTerminals: state.paymentTerminals,
        workstationNumberPortalRecords: state.workstationNumberPortalRecords,
        printerPortalRecords: state.printerPortalRecords,
      };

      const labels: Record<string, string> = {
        networkCircuits: 'Network Circuit Check',
        networkSwitches: 'Network Switch Check',
        ipads: 'iPad Switch Connectivity',
        ipadsMini: 'iPad Mini (CFD) Switch Connectivity',
        receiptPrinters: 'Receipt Printer Switch Connectivity',
        barcodeScanners: 'Barcode Scanners Switch Connectivity',
        cashDrawers: 'Cash Drawer Switch Connectivity',
        paymentTerminals: 'Payment Terminal Switch Connectivity',
        workstationNumberPortalRecords: 'NPOS Device Association Information',
        printerPortalRecords: 'NPOS Device Association Information',
      };

      Object.entries(devicesList).forEach(([name, list]) => {
        scanFeatures.add(labels[name]);

        list?.forEach(device => {
          if (device?.alerts?.length > 0) {
            device.alerts.forEach(alert => {
              if (alert.alertType !== AlertType.SCAN_FAILURE_ALERT) {
                issuesDiscovered.add(alert.alertDefinitionId);
              }
            });
          }
        });
      });

      return {
        scanDetails: {
          siteId,
          registerCount: state.registers?.length ?? 0,
          scanFeatures: Array.from(scanFeatures),
          issuesDiscovered: Array.from(issuesDiscovered),
        },
      };
    },
    [siteId]
  );

  useEffect(() => {
    if (!authState?.isAuthenticated || !siteId) {
      return;
    }

    const controller = new AbortController();
    const accessToken = oktaAuth.getAccessToken();
    const url = config.resourceServer.insightsApiUrlNPOS + '?siteId=' + siteId;

    async function fetchPOSData(accessToken: string | undefined, controller: AbortController) {
      fetch(url, {
        mode: 'cors',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        signal: controller.signal,
      })
        .then(response => {
          if (!response.ok) {
            return Promise.reject(response);
          }
          return response.json();
        })
        .then(data => {
          setData(data);
          dispatchUserLog('NPOS Register Scan', getScanLogPayload(data));
          setDataIsLoaded(true);
        })
        .catch(err => {
          if (err.name === 'AbortError') {
            const errorMsg: FetchResponseError = {
              ok: false,
              status: 'userAbortedRequest',
              statusText: 'DOMException: The user aborted a request.',
              type: '',
              url: config.resourceServer.insightsApiUrlNPOS + '?siteId=' + siteId,
            };
            setError(errorMsg);
          } else {
            setError(err);
          }
          setDataIsLoaded(false);
        });
    }

    setData(null);
    setError(null);
    setDataIsLoaded(false);
    fetchPOSData(accessToken, controller);

    return () => {
      controller.abort();
      setDataIsLoaded(false);
      setError(null);
      setData(null);
    };
  }, [siteId, authState, oktaAuth]);

  return {
    dataLoaded: dataIsLoaded,
    data,
    error,
  };
}

export default usePOS;
